
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skill } from '../../types/game';
import { GraduationCap, TrendingUp } from "lucide-react";
import SkillItem from './SkillItem';

interface SkillCategoryProps {
  category: string;
  skills: Skill[];
  getSkillStatus: (skill: Skill) => 'acquired' | 'locked' | 'insufficient-exp' | 'available';
  hasSkill: (skillId: string) => boolean;
  experience: number;
}

const SkillCategory: React.FC<SkillCategoryProps> = ({
  category,
  skills,
  getSkillStatus,
  hasSkill,
  experience
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Technical':
        return <TrendingUp className="mr-2 text-blue-400" />;
      case 'Business':
        return <TrendingUp className="mr-2 text-amber-400" />;
      case 'Operations':
        return <TrendingUp className="mr-2 text-green-400" />;
      case 'Leadership':
        return <TrendingUp className="mr-2 text-purple-400" />;
      default:
        return <GraduationCap className="mr-2" />;
    }
  };
  
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Technical':
        return 'from-blue-500/20 to-blue-600/5 border-blue-500/30';
      case 'Business':
        return 'from-amber-500/20 to-amber-600/5 border-amber-500/30';
      case 'Operations':
        return 'from-green-500/20 to-green-600/5 border-green-500/30';
      case 'Leadership':
        return 'from-purple-500/20 to-purple-600/5 border-purple-500/30';
      default:
        return 'from-muted/50 to-background border-white/10';
    }
  };
  
  return (
    <Card 
      className={`bg-gradient-to-br ${getCategoryColor(category)} backdrop-blur-sm shadow-md hover:shadow-lg transition-all`}
    >
      <CardHeader>
        <CardTitle className="flex items-center text-white">
          {getCategoryIcon(category)} {category} Skills
        </CardTitle>
        <CardDescription className="text-white/80">
          Master these skills to advance your career
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {skills.map((skill, index) => (
            <SkillItem
              key={skill.id}
              skill={skill}
              status={getSkillStatus(skill)}
              hasSkill={hasSkill}
              experience={experience}
              animationDelay={index * 0.1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillCategory;
